import { FileText, Folder } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCollections, useStars } from "~/api/queries";
import { Spinner } from "~/foundation/components/Spinner";
import { cn } from "~/foundation/utils/cn";

export interface StarredTreeProps {
  pathname: string;
  /** Mobile drawer closes on navigate; the desktop sidebar does nothing. */
  onNavigate?: (() => void) | undefined;
}

const ROW_CLASS =
  "flex h-8 min-w-0 flex-1 items-center gap-2 rounded-sm px-1.5 text-body text-sidebar-text " +
  "transition-colors duration-100 ease-in-out hover:bg-hover-bg motion-reduce:transition-none";

/**
 * W10 §2 — the Starred section: the viewer's own bookmarks, documents and collections together.
 *
 * `stars.list` side-loads the starred DOCUMENTS (so a title needs no second read) and no
 * collections at all — those are resolved against the collection list the shell already holds,
 * which is also what makes a star on a collection the viewer has since lost access to simply
 * disappear rather than render as a broken row.
 *
 * Server order is `updatedAt` DESC — most recently starred first. The `index` field exists for
 * manual ordering and the platform does not sort by it, so neither does this.
 */
export function StarredTree({ pathname, onNavigate }: StarredTreeProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useStars();
  const { data: collections } = useCollections();

  if (isLoading) {
    return (
      <div className="flex h-8 items-center px-1.5">
        <Spinner size={14} label={t("Loading")} />
      </div>
    );
  }

  const stars = data?.stars ?? [];
  const documents = new Map((data?.documents ?? []).map((document) => [document.id, document]));
  const collectionsById = new Map((collections ?? []).map((entry) => [entry.id, entry]));

  const rows = stars
    .map((star) => {
      if (star.documentId) {
        const document = documents.get(star.documentId);
        return document
          ? ({
              key: star.id,
              kind: "document" as const,
              id: document.id,
              label: document.title,
              emoji: document.emoji,
            } as const)
          : null;
      }
      if (star.collectionId) {
        const collection = collectionsById.get(star.collectionId);
        return collection
          ? ({
              key: star.id,
              kind: "collection" as const,
              id: collection.id,
              label: collection.name,
              emoji: collection.emoji,
            } as const)
          : null;
      }
      return null;
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  if (rows.length === 0) {
    return (
      <p className="flex h-8 items-center px-1.5 text-small text-text-tertiary">
        {t("Star a document to keep it here")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {rows.map((row) => {
        const href = row.kind === "document" ? `/documents/${row.id}` : `/collections/${row.id}`;
        const active = pathname === href;
        return (
          <li key={row.key} className="flex items-center">
            {row.kind === "document" ? (
              <Link
                to="/documents/$documentId"
                params={{ documentId: row.id }}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(ROW_CLASS, active && "bg-sidebar-active-bg text-text")}
              >
                {row.emoji ? (
                  <span aria-hidden className="w-4 shrink-0 text-center">
                    {row.emoji}
                  </span>
                ) : (
                  <FileText size={16} aria-hidden className="shrink-0" />
                )}
                <span className="truncate">{row.label}</span>
              </Link>
            ) : (
              <Link
                to="/collections/$collectionId"
                params={{ collectionId: row.id }}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(ROW_CLASS, active && "bg-sidebar-active-bg text-text")}
              >
                {row.emoji ? (
                  <span aria-hidden className="w-4 shrink-0 text-center">
                    {row.emoji}
                  </span>
                ) : (
                  <Folder size={16} aria-hidden className="shrink-0" />
                )}
                <span className="truncate">{row.label}</span>
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
